// AI API service for image generation using OpenAI DALL-E 3
// Uses shared API key from Supabase Edge Functions

import { getOpenAIApiKey } from '../lib/supabase'

const OPENAI_API_URL = 'https://api.openai.com/v1/images/generations'

export interface GenerateImageParams {
  prompt: string
  uploadedImages?: File[]
  filters: {
    background: string[]
    nailColor: string[]
    jewelry: string[]
    shoes: string[]
    skinColor: string[]
    angle: string[]
    style: string[]
    foodCoverage: string[]
    fantasyTransformation: string[]
    glamorEffects: string[]
    commonFilters: string[]
    photoEffects: string[]
    nailLength: string[]
    furTexture: string[]
    surfaceTexture: string[]
    bondage: string[]
    occupation: string[]
    toeHat: string[]
    paraphernalia: string[]
  }
}

export interface GenerationResult {
  imageUrl: string
  model: string
  isMock?: boolean
}

// Create a simple SVG fallback image
const createFallbackImage = (prompt: string): string => {
  const encodedPrompt = encodeURIComponent(prompt.substring(0, 50))
  
  const svg = `
    <svg width="512" height="512" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#667eea;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="512" height="512" fill="url(#bg)"/>
      <text x="256" y="200" font-family="Arial, sans-serif" font-size="24" fill="white" text-anchor="middle">
        AI Generation
      </text>
      <text x="256" y="240" font-family="Arial, sans-serif" font-size="16" fill="white" text-anchor="middle">
        Temporarily Unavailable
      </text>
      <text x="256" y="300" font-family="Arial, sans-serif" font-size="14" fill="white" text-anchor="middle">
        ${prompt.substring(0, 30)}${prompt.length > 30 ? '...' : ''}
      </text>
      <text x="256" y="350" font-family="Arial, sans-serif" font-size="12" fill="rgba(255,255,255,0.8)" text-anchor="middle">
        Please try again later
      </text>
    </svg>
  `
  
  return `data:image/svg+xml;base64,${btoa(svg)}`
}

// Helper function to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
}

// Helper function to analyze uploaded images using GPT-4 Vision
const analyzeUploadedImages = async (images: File[], apiKey: string): Promise<string> => {
  if (!images || images.length === 0) return ''
  
  try {
    console.log(`🔍 Analyzing ${images.length} uploaded image(s) with GPT-4 Vision...`)
    
    // Convert images to base64
    const imagePromises = images.map(async (image) => {
      const base64 = await fileToBase64(image)
      return {
        type: "image_url",
        image_url: {
          url: base64,
          detail: "low" // Use low detail for faster processing
        }
      }
    })
    
    const imageContents = await Promise.all(imagePromises)
    
    // Create GPT-4 Vision request
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4-vision-preview',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: 'Analyze these images and describe their key visual elements, colors, textures, poses, lighting, and style. Focus on details that would help recreate similar images. Be descriptive but concise. This will be used as context for AI image generation.'
              },
              ...imageContents
            ]
          }
        ],
        max_tokens: 500
      })
    })
    
    if (!response.ok) {
      console.log('⚠️ GPT-4 Vision analysis failed, using basic description')
      return `Based on ${images.length} reference image(s) uploaded by user`
    }
    
    const data = await response.json()
    const analysis = data.choices?.[0]?.message?.content || ''
    
    console.log('✅ GPT-4 Vision analysis complete')
    return analysis
    
  } catch (error) {
    console.error('❌ Error analyzing uploaded images:', error)
    return `Based on ${images.length} reference image(s) uploaded by user`
  }
}

// Updated buildEnhancedPrompt function to handle uploaded images
const buildEnhancedPrompt = async (
  userPrompt: string, 
  filters: GenerateImageParams['filters'],
  uploadedImages?: File[],
  apiKey?: string
): Promise<string> => {
  let enhancedPrompt = userPrompt

  // Analyze uploaded images if provided and API key is available
  if (uploadedImages && uploadedImages.length > 0 && apiKey) {
    try {
      const imageAnalysis = await analyzeUploadedImages(uploadedImages, apiKey)
      if (imageAnalysis) {
        enhancedPrompt = `${userPrompt}\n\nReference image analysis: ${imageAnalysis}`
      }
    } catch (error) {
      console.log('⚠️ Image analysis failed, proceeding without it')
      enhancedPrompt = `${userPrompt}\n\nBased on ${uploadedImages.length} reference image(s) provided by user`
    }
  } else if (uploadedImages && uploadedImages.length > 0) {
    enhancedPrompt = `${userPrompt}\n\nBased on ${uploadedImages.length} reference image(s) provided by user`
  }

  // Apply filters (existing filter logic)
  const filterDescriptions: string[] = []

  // Background filters
  if (filters.background && filters.background.length > 0) {
    const backgroundMap: Record<string, string> = {
      'beach': 'sandy beach with ocean waves',
      'forest': 'lush green forest with trees',
      'city': 'urban cityscape with buildings',
      'bedroom': 'cozy bedroom interior',
      'kitchen': 'modern kitchen setting',
      'studio': 'professional photography studio with lighting',
      'garden': 'beautiful garden with flowers',
      'pool': 'swimming pool area',
      'sunset': 'golden sunset lighting',
      'night': 'nighttime scene with ambient lighting'
    }
    
    filters.background.forEach(bg => {
      if (backgroundMap[bg]) {
        filterDescriptions.push(`Background: ${backgroundMap[bg]}`)
      }
    })
  }

  // Nail color filters
  if (filters.nailColor && filters.nailColor.length > 0) {
    const nailColorMap: Record<string, string> = {
      'red': 'vibrant red nail polish',
      'pink': 'soft pink nail polish',
      'nude': 'nude/natural nail polish',
      'black': 'sleek black nail polish',
      'white': 'clean white nail polish',
      'blue': 'ocean blue nail polish',
      'purple': 'royal purple nail polish',
      'green': 'emerald green nail polish',
      'gold': 'metallic gold nail polish',
      'silver': 'shimmery silver nail polish',
      'rainbow': 'colorful rainbow nail art',
      'french': 'classic French manicure',
      'ombre': 'gradient ombre nail design',
      'glitter': 'sparkling glitter nail polish',
      'matte': 'matte finish nail polish'
    }
    
    filters.nailColor.forEach(color => {
      if (nailColorMap[color]) {
        filterDescriptions.push(`Nail color: ${nailColorMap[color]}`)
      }
    })
  }

  // Jewelry filters
  if (filters.jewelry && filters.jewelry.length > 0) {
    const jewelryMap: Record<string, string> = {
      'anklet': 'delicate anklet jewelry',
      'toe-ring': 'elegant toe ring',
      'chain': 'decorative chain jewelry',
      'bracelet': 'ankle bracelet',
      'charm': 'charm bracelet or anklet',
      'pearl': 'pearl jewelry accessories',
      'diamond': 'diamond jewelry accents',
      'gold': 'gold jewelry pieces',
      'silver': 'silver jewelry accessories',
      'rose-gold': 'rose gold jewelry'
    }
    
    filters.jewelry.forEach(jewelry => {
      if (jewelryMap[jewelry]) {
        filterDescriptions.push(`Jewelry: ${jewelryMap[jewelry]}`)
      }
    })
  }

  // Shoes filters - comprehensive mapping
  if (filters.shoes && filters.shoes.length > 0) {
    const shoesMap: Record<string, string> = {
      // Athletic & Sports
      'running-shoes': 'modern running shoes with breathable mesh',
      'sneakers': 'casual sneakers with clean lines',
      'cross-trainers': 'versatile cross-training shoes',
      'basketball-shoes': 'high-top basketball shoes',
      'tennis-shoes': 'court tennis shoes',
      'hiking-boots': 'rugged hiking boots with ankle support',
      'climbing-shoes': 'specialized rock climbing shoes',
      'cycling-shoes': 'clipless cycling shoes',
      'golf-shoes': 'spiked golf shoes',
      'soccer-cleats': 'soccer cleats with studs',
      'football-cleats': 'football cleats',
      'baseball-cleats': 'baseball cleats',
      'track-spikes': 'track and field spike shoes',
      'wrestling-shoes': 'lightweight wrestling shoes',
      'boxing-shoes': 'high-top boxing shoes',
      'martial-arts-shoes': 'flexible martial arts shoes',
      'dance-sneakers': 'dance sneakers with split sole',
      'cheerleading-shoes': 'cheerleading shoes',
      'skateboarding-shoes': 'skateboarding shoes with grip',
      'water-shoes': 'water shoes for aquatic activities',
      
      // Heels & Formal
      'stilettos': 'elegant stiletto heels',
      'pumps': 'classic pump heels',
      'platform-heels': 'platform heels with height',
      'wedges': 'wedge heels for comfort',
      'kitten-heels': 'low kitten heels',
      'block-heels': 'stable block heels',
      'cone-heels': 'cone-shaped heels',
      'needle-heels': 'ultra-thin needle heels',
      'chunky-heels': 'thick chunky heels',
      'ankle-strap-heels': 'heels with ankle straps',
      'mary-jane-heels': 'Mary Jane style heels',
      'peep-toe-heels': 'peep-toe heels',
      'open-toe-heels': 'open-toe heels',
      'pointed-toe-heels': 'pointed-toe heels',
      'round-toe-heels': 'round-toe heels',
      'square-toe-heels': 'square-toe heels',
      'slingback-heels': 'slingback heels',
      'strappy-heels': 'strappy heels',
      'court-shoes': 'classic court shoes',
      'designer-heels': 'luxury designer heels',
      
      // Boots
      'ankle-boots': 'stylish ankle boots',
      'knee-high-boots': 'knee-high boots',
      'thigh-high-boots': 'thigh-high boots',
      'over-knee-boots': 'over-the-knee boots',
      'combat-boots': 'military-style combat boots',
      'doc-martens': 'Doc Martens style boots',
      'chelsea-boots': 'sleek Chelsea boots',
      'cowboy-boots': 'Western cowboy boots',
      'riding-boots': 'equestrian riding boots',
      'rain-boots': 'waterproof rain boots',
      'snow-boots': 'insulated snow boots',
      'work-boots': 'sturdy work boots',
      'steel-toe-boots': 'steel-toe work boots',
      'desert-boots': 'desert boots',
      'chukka-boots': 'chukka boots',
      'lace-up-boots': 'lace-up boots',
      'zip-up-boots': 'zip-up boots',
      'platform-boots': 'platform boots',
      'gothic-boots': 'gothic-style boots',
      'punk-boots': 'punk rock boots',
      'biker-boots': 'motorcycle biker boots',
      'military-boots': 'military combat boots',
      'tactical-boots': 'tactical boots',
      'hunting-boots': 'hunting boots',
      'fishing-boots': 'fishing waders',
      'fashion-boots': 'fashion boots',
      'designer-boots': 'designer boots',
      'vintage-boots': 'vintage-style boots',
      'steampunk-boots': 'steampunk-style boots',
      'victorian-boots': 'Victorian-era boots',
      
      // Sandals
      'flip-flops': 'casual flip-flops',
      'slides': 'slip-on slides',
      'gladiator-sandals': 'gladiator-style sandals',
      'strappy-sandals': 'strappy sandals',
      'platform-sandals': 'platform sandals',
      'wedge-sandals': 'wedge sandals',
      'flat-sandals': 'flat sandals',
      'thong-sandals': 'thong sandals',
      'sport-sandals': 'sport sandals',
      'hiking-sandals': 'hiking sandals',
      'water-sandals': 'water sandals',
      'beach-sandals': 'beach sandals',
      'espadrilles': 'espadrille sandals',
      'cork-sandals': 'cork-soled sandals',
      'leather-sandals': 'leather sandals',
      'rubber-sandals': 'rubber sandals',
      'designer-sandals': 'designer sandals',
      'bohemian-sandals': 'bohemian-style sandals',
      'ethnic-sandals': 'ethnic-style sandals',
      'minimalist-sandals': 'minimalist sandals',
      'luxury-sandals': 'luxury sandals',
      'comfort-sandals': 'comfort sandals',
      'orthopedic-sandals': 'orthopedic sandals',
      'walking-sandals': 'walking sandals',
      'dress-sandals': 'dress sandals',
      'casual-sandals': 'casual sandals',
      
      // Flats & Casual
      'ballet-flats': 'classic ballet flats',
      'loafers': 'comfortable loafers',
      'moccasins': 'soft moccasins',
      'boat-shoes': 'nautical boat shoes',
      'slip-ons': 'easy slip-on shoes',
      'canvas-shoes': 'canvas shoes',
      'espadrilles-flats': 'flat espadrilles',
      'driving-shoes': 'driving shoes',
      'smoking-slippers': 'smoking slippers',
      'pointed-flats': 'pointed-toe flats',
      'round-flats': 'round-toe flats',
      'square-flats': 'square-toe flats',
      'cap-toe-flats': 'cap-toe flats',
      'bow-flats': 'bow-detail flats',
      'buckle-flats': 'buckle flats',
      'strap-flats': 'strap flats',
      'mary-jane-flats': 'Mary Jane flats',
      'oxford-flats': 'Oxford-style flats',
      'brogue-flats': 'brogue flats',
      'penny-loafers': 'penny loafers',
      'tassel-loafers': 'tassel loafers',
      'bit-loafers': 'bit loafers',
      'chain-loafers': 'chain loafers',
      'comfort-flats': 'comfort flats',
      'walking-flats': 'walking flats',
      
      // Formal & Dress
      'oxford-shoes': 'classic Oxford dress shoes',
      'brogues': 'detailed brogue shoes',
      'dress-shoes': 'formal dress shoes',
      'patent-leather': 'patent leather dress shoes',
      'tuxedo-shoes': 'tuxedo shoes',
      'formal-pumps': 'formal pumps',
      'evening-shoes': 'evening dress shoes',
      'cocktail-shoes': 'cocktail party shoes',
      'wedding-shoes': 'wedding shoes',
      'prom-shoes': 'prom shoes',
      'formal-sandals': 'formal sandals',
      'dress-boots': 'dress boots',
      'formal-flats': 'formal flats',
      'business-shoes': 'business shoes',
      'professional-shoes': 'professional shoes',
      
      // Specialty & Unique
      'platform-shoes': 'platform shoes with height',
      'creepers': 'creeper shoes',
      'goth-shoes': 'gothic shoes',
      'punk-shoes': 'punk rock shoes',
      'emo-shoes': 'emo-style shoes',
      'scene-shoes': 'scene-style shoes',
      'alternative-shoes': 'alternative fashion shoes',
      'vintage-shoes': 'vintage-style shoes',
      'retro-shoes': 'retro shoes',
      'pin-up-shoes': 'pin-up style shoes',
      'rockabilly-shoes': 'rockabilly shoes',
      'swing-shoes': 'swing dance shoes',
      'lindy-hop-shoes': 'lindy hop shoes',
      'ballroom-shoes': 'ballroom dance shoes',
      'latin-shoes': 'Latin dance shoes',
      'tango-shoes': 'tango shoes',
      'salsa-shoes': 'salsa dance shoes',
      'tap-shoes': 'tap dance shoes',
      'ballet-shoes': 'ballet shoes',
      'pointe-shoes': 'pointe shoes',
      'jazz-shoes': 'jazz dance shoes',
      'modern-dance-shoes': 'modern dance shoes',
      'character-shoes': 'character dance shoes',
      'theatrical-shoes': 'theatrical shoes',
      'costume-shoes': 'costume shoes',
      'cosplay-shoes': 'cosplay shoes',
      
      // Fetish & Alternative
      'fetish-heels': 'fetish-style heels',
      'dominatrix-boots': 'dominatrix boots',
      'latex-shoes': 'latex shoes',
      'leather-boots': 'leather boots',
      'corset-boots': 'corset-style boots',
      'lace-up-heels': 'lace-up heels',
      'bondage-shoes': 'bondage-style shoes',
      'platform-boots-extreme': 'extreme platform boots',
      'stripper-heels': 'stripper heels',
      'pole-dance-shoes': 'pole dance shoes',
      'exotic-dance-shoes': 'exotic dance shoes',
      'burlesque-shoes': 'burlesque shoes',
      'cabaret-shoes': 'cabaret shoes',
      'showgirl-shoes': 'showgirl shoes',
      'drag-shoes': 'drag queen shoes',
      'cross-dress-shoes': 'cross-dressing shoes',
      'sissy-shoes': 'sissy shoes',
      'femdom-shoes': 'femdom shoes',
      'mistress-shoes': 'mistress shoes',
      'slave-shoes': 'slave shoes',
      'submissive-shoes': 'submissive shoes',
      
      // Fantasy & Cosplay
      'fairy-shoes': 'fairy-tale shoes',
      'elf-shoes': 'elf shoes with pointed toes',
      'pixie-shoes': 'pixie shoes',
      'witch-shoes': 'witch shoes',
      'wizard-shoes': 'wizard shoes',
      'medieval-shoes': 'medieval-style shoes',
      'renaissance-shoes': 'Renaissance-era shoes',
      'pirate-boots': 'pirate boots',
      'knight-boots': 'knight armor boots',
      'dragon-shoes': 'dragon-themed shoes',
      'unicorn-shoes': 'unicorn-themed shoes',
      'mermaid-shoes': 'mermaid-themed shoes',
      'angel-shoes': 'angel-themed shoes',
      'demon-shoes': 'demon-themed shoes',
      'vampire-shoes': 'vampire-themed shoes',
      'werewolf-shoes': 'werewolf-themed shoes',
      'zombie-shoes': 'zombie-themed shoes',
      'superhero-shoes': 'superhero shoes',
      'anime-shoes': 'anime-style shoes',
      
      // Futuristic & Sci-Fi
      'cyberpunk-shoes': 'cyberpunk-style shoes',
      'futuristic-boots': 'futuristic boots',
      'space-boots': 'space-age boots',
      'robot-shoes': 'robot-themed shoes',
      'alien-shoes': 'alien-themed shoes',
      'holographic-shoes': 'holographic shoes',
      'led-shoes': 'LED light-up shoes',
      'neon-shoes': 'neon-colored shoes',
      'metallic-shoes': 'metallic shoes',
      'chrome-shoes': 'chrome-finished shoes',
      'mirror-shoes': 'mirror-finished shoes',
      'transparent-shoes': 'transparent shoes',
      'glass-shoes': 'glass-like shoes',
      'crystal-shoes': 'crystal-themed shoes',
      'diamond-shoes': 'diamond-encrusted shoes',
      'gold-shoes': 'gold-plated shoes',
      'silver-shoes': 'silver-plated shoes',
      'copper-shoes': 'copper-finished shoes',
      'bronze-shoes': 'bronze-finished shoes',
      'iridescent-shoes': 'iridescent shoes',
      
      // Extreme & Unusual
      'impossible-heels': 'impossibly high heels',
      'architectural-shoes': 'architectural shoes',
      'sculptural-shoes': 'sculptural shoes',
      'artistic-shoes': 'artistic shoes',
      'avant-garde-shoes': 'avant-garde shoes',
      'conceptual-shoes': 'conceptual shoes',
      'experimental-shoes': 'experimental shoes',
      'unconventional-shoes': 'unconventional shoes',
      'bizarre-shoes': 'bizarre shoes',
      'weird-shoes': 'weird shoes',
      'strange-shoes': 'strange shoes',
      'unusual-shoes': 'unusual shoes',
      'odd-shoes': 'odd shoes',
      'quirky-shoes': 'quirky shoes',
      'eccentric-shoes': 'eccentric shoes',
      'outrageous-shoes': 'outrageous shoes',
      'shocking-shoes': 'shocking shoes',
      'controversial-shoes': 'controversial shoes',
      'provocative-shoes': 'provocative shoes',
      'edgy-shoes': 'edgy shoes',
      'radical-shoes': 'radical shoes',
      'extreme-shoes': 'extreme shoes',
      'insane-shoes': 'insane shoes',
      'crazy-shoes': 'crazy shoes',
      'wild-shoes': 'wild shoes',
      'mad-shoes': 'mad shoes',
      'psychedelic-shoes': 'psychedelic shoes',
      'surreal-shoes': 'surreal shoes',
      'abstract-shoes': 'abstract shoes',
      'geometric-shoes': 'geometric shoes',
      'asymmetric-shoes': 'asymmetric shoes',
      'deconstructed-shoes': 'deconstructed shoes',
      'reconstructed-shoes': 'reconstructed shoes',
      'hybrid-shoes': 'hybrid shoes',
      'mutant-shoes': 'mutant shoes',
      'alien-hybrid-shoes': 'alien-hybrid shoes',
      'bio-mechanical-shoes': 'bio-mechanical shoes',
      'organic-shoes': 'organic-shaped shoes',
      'living-shoes': 'living shoes',
      'breathing-shoes': 'breathing shoes',
      'pulsating-shoes': 'pulsating shoes',
      'morphing-shoes': 'morphing shoes',
      'transforming-shoes': 'transforming shoes',
      'shape-shifting-shoes': 'shape-shifting shoes',
      'color-changing-shoes': 'color-changing shoes',
      'temperature-sensitive-shoes': 'temperature-sensitive shoes',
      'mood-shoes': 'mood-responsive shoes',
      'emotion-shoes': 'emotion-responsive shoes',
      'thought-controlled-shoes': 'thought-controlled shoes',
      'ai-shoes': 'AI-powered shoes',
      'smart-shoes': 'smart shoes',
      'connected-shoes': 'connected shoes',
      'iot-shoes': 'IoT shoes',
      'augmented-reality-shoes': 'augmented reality shoes',
      'virtual-reality-shoes': 'virtual reality shoes',
      'mixed-reality-shoes': 'mixed reality shoes',
      'hologram-shoes': 'hologram shoes',
      'projection-shoes': 'projection shoes',
      'laser-shoes': 'laser shoes',
      'plasma-shoes': 'plasma shoes',
      'energy-shoes': 'energy shoes',
      'force-field-shoes': 'force field shoes',
      'anti-gravity-shoes': 'anti-gravity shoes',
      'levitating-shoes': 'levitating shoes',
      'floating-shoes': 'floating shoes',
      'flying-shoes': 'flying shoes',
      'rocket-shoes': 'rocket shoes',
      'jet-shoes': 'jet-powered shoes',
      'propeller-shoes': 'propeller shoes',
      'wing-shoes': 'winged shoes',
      'feather-shoes': 'feathered shoes',
      'fur-shoes': 'fur-covered shoes',
      'scale-shoes': 'scale-covered shoes',
      'spike-shoes': 'spiked shoes',
      'thorn-shoes': 'thorn-covered shoes',
      'razor-shoes': 'razor-edged shoes',
      'blade-shoes': 'blade shoes',
      'sword-shoes': 'sword shoes',
      'weapon-shoes': 'weapon shoes',
      'armor-shoes': 'armor shoes',
      'shield-shoes': 'shield shoes',
      'fortress-shoes': 'fortress shoes',
      'castle-shoes': 'castle shoes',
      'tower-shoes': 'tower shoes',
      'skyscraper-shoes': 'skyscraper shoes',
      'mountain-shoes': 'mountain-shaped shoes',
      'volcano-shoes': 'volcano shoes',
      'earthquake-shoes': 'earthquake shoes',
      'tsunami-shoes': 'tsunami shoes',
      'hurricane-shoes': 'hurricane shoes',
      'tornado-shoes': 'tornado shoes',
      'lightning-shoes': 'lightning shoes',
      'thunder-shoes': 'thunder shoes',
      'storm-shoes': 'storm shoes',
      'rain-shoes': 'rain shoes',
      'snow-shoes-extreme': 'extreme snow shoes',
      'ice-shoes': 'ice shoes',
      'fire-shoes': 'fire shoes',
      'lava-shoes': 'lava shoes',
      'magma-shoes': 'magma shoes'
    }
    
    filters.shoes.forEach(shoe => {
      if (shoesMap[shoe]) {
        filterDescriptions.push(`Footwear: ${shoesMap[shoe]}`)
      }
    })
  }

  // Add other filter categories with similar comprehensive mappings...
  // (Similar detailed mappings for other filter categories would go here)

  // Combine all descriptions
  if (filterDescriptions.length > 0) {
    enhancedPrompt += `\n\nAdditional details: ${filterDescriptions.join(', ')}`
  }

  // Add general style instructions
  enhancedPrompt += '\n\nStyle: High-quality, professional photography, detailed, artistic composition'

  console.log('🎨 Enhanced prompt created:', enhancedPrompt.substring(0, 200) + '...')
  return enhancedPrompt
}

export const generateFeetImage = async (params: GenerateImageParams): Promise<GenerationResult> => {
  try {
    console.log('🚀 Starting image generation...')
    
    // Try to get API key from Supabase Edge Function (with timeout)
    let apiKey: string | null = null
    try {
      console.log('🔑 Attempting to retrieve API key from Supabase...')
      apiKey = await Promise.race([
        getOpenAIApiKey(),
        new Promise<null>((_, reject) => 
          setTimeout(() => reject(new Error('Supabase API key timeout')), 5000)
        )
      ])
      
      if (apiKey) {
        console.log('✅ Using Supabase API key')
      }
    } catch (error) {
      console.log('⚠️ Supabase API key unavailable:', error instanceof Error ? error.message : 'Unknown error')
    }
    
    if (!apiKey) {
      console.log('❌ No API key available, using mock generation')
      const mockUrl = await generateMockImage()
      return { imageUrl: mockUrl, model: 'mock-generation-no-key', isMock: true }
    }

    // Build the enhanced prompt with filters and uploaded images
    const enhancedPrompt = await buildEnhancedPrompt(params.prompt, params.filters, params.uploadedImages, apiKey)

    console.log('🎨 Generating image with Supabase API key...')

    // Try DALL-E 3 generation
    const response = await fetch(OPENAI_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'dall-e-3',
        prompt: enhancedPrompt,
        n: 1,
        size: '1024x1024',
        quality: 'hd',
        style: 'vivid'
      }),
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('❌ OpenAI API error:', response.status, response.statusText, errorData)
      throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    
    if (!data.data || !data.data[0] || !data.data[0].url) {
      console.error('❌ Invalid response format from OpenAI API:', data)
      throw new Error('Invalid response format from OpenAI API')
    }
    
    console.log('✅ Successfully generated image with Supabase API key')
    
    return { 
      imageUrl: data.data[0].url, 
      model: 'DALL-E 3 (Supabase)' 
    }
    
  } catch (error) {
    console.error('❌ Error generating image with OpenAI:', error)
    
    // Fallback to mock generation
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    console.log(`🔄 Falling back to mock generation due to error: ${errorMessage}`)
    
    try {
      const mockUrl = await generateMockImage()
      return { imageUrl: mockUrl, model: 'mock-generation-fallback', isMock: true }
    } catch (mockError) {
      console.error('❌ Mock generation also failed:', mockError)
      // Return a more reliable fallback
      console.log('🔄 Using basic SVG fallback')
      return { 
        imageUrl: createFallbackImage(params.prompt), 
        model: 'svg-fallback', 
        isMock: true 
      }
    }
  }
}

// Mock generation function (existing code)
export const generateMockImage = async (): Promise<string> => {
  // Create a simple colored rectangle as a placeholder
  const canvas = document.createElement('canvas')
  canvas.width = 512
  canvas.height = 512
  const ctx = canvas.getContext('2d')
  
  if (ctx) {
    // Generate a random color
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8']
    const randomColor = colors[Math.floor(Math.random() * colors.length)]
    
    ctx.fillStyle = randomColor
    ctx.fillRect(0, 0, 512, 512)
    
    // Add some text
    ctx.fillStyle = 'white'
    ctx.font = '24px Arial'
    ctx.textAlign = 'center'
    ctx.fillText('Mock Generated Image', 256, 256)
  }
  
  return canvas.toDataURL()
}

export { saveImage } from '../lib/supabase'

export default {
  generateFeetImage,
  generateMockImage
} 